<template>
  <div class="mx-auto mb-6 max-w-screen-md text-neutral-900 mobile:m-3">
    <form
      autocomplete="off"
      @submit.prevent="handleForm"
    >
      <h3 class="relative mb-8 mt-2">
        <span class="absolute -mt-2 size-[41px] border-[5px] border-primary-700" />
        <span class="relative ml-3 block bg-white">{{ $t('Create a new KiK account') }}</span>
      </h3>
      <KikDynamicForm
        ref="dynamicBillingForm"
        :fields="fields"
        :values="form"
        :by-key-order="billingFormKeys"
        @on-form-update="onFormUpdate"
      />
      <h5 class="mb-6 mt-8">
        {{ $t('My personal data') }}:
      </h5>
      <KikDynamicForm
        ref="dynamicForm"
        :fields="fields"
        :values="form"
        :by-key-order="['dateOfBirth', 'email', 'emailConfirmation', 'password', 'passwordConfirmation', 'mobile', 'phone']"
        @on-form-update="onFormUpdate"
      />
      <div
        v-if="formErrors.register"
        class="text-negative-500"
      >
        {{ $t(formErrors.register) }}
      </div>
      <div
        v-if="registrationError.save"
        class="text-negative-500"
      >
        {{ $t('An error occured') }}
      </div>
      <KikButton
        class="fixed bottom-4 right-4 mt-6 w-[226px] mobile:w-[44%]"
        type="submit"
        data-testid="register__submit-btn"
        :disabled="registrationLoading"
      >
        <SfLoaderCircular v-if="registrationLoading" />
        <div v-else>
          {{ $t('Register') }}
        </div>
      </KikButton>
    </form>
  </div>
</template>

<script setup lang="ts">
import { v4 as uuidV4 } from 'uuid';
import { SfLoaderCircular } from '@storefront-ui/vue';
import { checkoutForm } from '~/components/Kik/DynamicForm/checkout-form';
import { useUser } from '~/composables/custom/useUser/useUser';
import useUiNotification from '~/composables/useUiNotification';
import { getErrorMessage } from '~/helpers/utils/getErrorMessage';
import { useTracking } from '~/composables/custom/useTracking';

defineComponent({ name: 'RegisterPage' });

const { $i18n } = useNuxtApp();
const { trackError, trackRegistered } = useTracking();

const {
  error: registrationError,
  updating: registrationLoading,
  registerUser,
} = useUser();
const { send } = useUiNotification();

const form = ref({
  key: uuidV4(),
  userType: '',
  salutation: '',
  title: '',
  firstName: '',
  lastName: '',
  streetName: '',
  streetNumber: '',
  additionalStreetInfo: '',
  postalCode: '',
  city: '',
  country: '',
  companyName: '',
  vatId: '',
  dateOfBirth: '',
  email: '',
  emailConfirmation: '',
  password: '',
  passwordConfirmation: '',
  mobile: '',
  phone: '',
});

const dynamicForm = ref();
const dynamicBillingForm = ref();

const errors = reactive<{ register: string | undefined }>({
  register: undefined,
});

const isInvalidForm = async () => {
  return dynamicBillingForm.value.isInvalid() || dynamicForm.value.isInvalid();
};

const handleForm = async () => {
  if (await isInvalidForm()) {
    const errorsBilling = dynamicBillingForm.value.errors();
    const errorsPersonal = dynamicForm.value.errors();

    Object.entries<string>(errorsBilling).forEach(([key, value]) => trackError(`Form input - registration billing - ${key}`, value));
    Object.entries<string>(errorsPersonal).forEach(([key, value]) => trackError(`Form input - registration personal - ${key}`, value));
    return;
  }
  errors.register = undefined;

  try {
    await registerUser({ ...form.value, vatId: form.value?.vatId?.replaceAll(/\D/g, '') });
    const errorMessage = registrationError.value.registerUser;
    if (errorMessage) {
      const message = $i18n.t(getErrorMessage(errorMessage));
      errors.register = message;
      trackError('registration', message);
      return;
    }
    if (!registrationError.value.registerUser) {
      send({
        message: 'The user was registered with success',
        icon: 'profile_filled',
        type: 'success',
      });
      trackRegistered();
      await navigateTo('/');
    }
  }
  catch (err) {
    if (err instanceof Error) {
      errors.register = err.message;
      trackError('registration', err.message);
    }
  }
};

const billingFormKeys = computed(() => {
  if (form.value.userType !== 'company') {
    return ['userType', 'salutation', 'title', 'firstName', 'lastName', 'streetName', 'streetNumber', 'additionalStreetInfo', 'postalCode', 'city', 'country'];
  }

  return ['userType', 'companyName', 'salutation', 'title', 'firstName', 'lastName', 'streetName', 'streetNumber', 'additionalStreetInfo', 'postalCode', 'city', 'country', 'vatId'];
});

const formErrors = computed(() => errors);
const fields = computed(() => checkoutForm(form.value));
const onFormUpdate = (value: any) => {
  form.value = {
    ...form.value,
    ...value,
  };
};
</script>
